import React, { FC, useCallback } from 'react'
import { Pressable } from 'react-native'
import { useSearchParams } from 'react-router-dom'

import { Trans } from '@common/config/localization'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import { THEME_TYPES } from '@common/styles/themeConfig'
import flexbox from '@common/styles/utils/flexbox'

import { PendingBalanceBadge } from '../PendingBalanceBadge'

export interface RailgunShieldPendingBadgeProps {
  processingMultiCount: number | null
  badgeText: string | null
  /**
   * Optional override for the "Activity" link press. In the extension dashboard
   * Activity is a tab on the same screen, so the default just sets `tab=activity`.
   * Mobile-web has Activity as a separate route and passes a navigate handler.
   */
  onActivityPress?: () => void
}

const RailgunShieldPendingBadge: FC<RailgunShieldPendingBadgeProps> = ({
  processingMultiCount,
  badgeText,
  onActivityPress
}) => {
  const { theme, themeType } = useTheme()
  const [, setSearchParams] = useSearchParams()
  const goToActivityTab = useCallback(() => {
    if (onActivityPress) {
      onActivityPress()
      return
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', 'activity')
      return next
    })
  }, [onActivityPress, setSearchParams])

  const labelColor =
    themeType === THEME_TYPES.DARK ? theme.primaryBackgroundInverted : theme.primaryBackground

  return (
    <PendingBalanceBadge>
      {processingMultiCount != null ? (
        <Pressable
          onPress={goToActivityTab}
          hitSlop={6}
          style={[flexbox.directionRow, flexbox.alignCenter, flexbox.wrap]}
        >
          <Text fontSize={12} weight="number_medium" style={{ lineHeight: 14 }} color={labelColor}>
            <Trans
              i18nKey="{{count}} processing in <0>Activity</0>"
              count={processingMultiCount}
              components={[
                <Text
                  key="activity-link"
                  fontSize={12}
                  weight="number_medium"
                  style={{ lineHeight: 14 }}
                  color={labelColor}
                  underline
                />
              ]}
            />
          </Text>
        </Pressable>
      ) : (
        <Text fontSize={12} weight="number_medium" style={{ lineHeight: 14 }} color={labelColor}>
          {badgeText}
        </Text>
      )}
    </PendingBalanceBadge>
  )
}

export default RailgunShieldPendingBadge
